import { useEffect, useState } from "react";
import { X } from "lucide-react";
import Modal from "../Modal";
import Btn from "../Btn";
import Select2 from "../Select2";
import { todoTaskApi } from "../../api/todoTaskApi";
import OutsidePartyPicker from "./OutsidePartyPicker";
import {
  Field, ImageTray, Lightbox, PRIORITIES, PRIORITY, errMsg, inputStyle, notifyTodoChanged, unwrap,
  useStagedFiles,
} from "./todoShared";

/**
 * New task, or edit one (title, details, who, priority, due date).
 *
 * New: WHO does it —
 *   • In the factory: one or more colleagues (yourself by default). Each gets
 *     their own copy, linked as a group ("2/3 done").
 *   • Outside the factory: a person from the OUTSIDE PEOPLE list (they have no
 *     login; "+ New" adds one on the spot). It goes on YOUR list and you mark
 *     it done or not done yourself. Older tasks may have who / where typed in
 *     instead — kept as it is until someone is picked.
 * Files can be attached (photos or any document) — they go on every copy's
 * first history line. Each person then accepts the task with the date they
 * will finish by, or declines it with a reason.
 *
 * Edit: the assigner only, while the task is being worked on or after it was
 * declined (the server's `canEdit`). Giving a copy to someone else starts it
 * afresh for them; saving a DECLINED task offers it again (to the same
 * person or the one chosen). On a group copy the title, details, priority
 * and due date can go to the other open copies too (ticked by default).
 */
export default function TodoTaskFormModal({ open, onClose, onSaved, task }) {
  const editing = Boolean(task);
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [where, setWhere] = useState("IN");          // IN | OUT
  const [people, setPeople] = useState([]);          // user ids as strings
  const [party, setParty] = useState("");            // typed on older tasks
  const [partyPick, setPartyPick] = useState(null);  // { id, label } from the list
  const [priority, setPriority] = useState("NORMAL");
  const [dueDate, setDueDate] = useState("");
  const [applyToGroup, setApplyToGroup] = useState(true);
  const [users, setUsers] = useState([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [lightbox, setLightbox] = useState(null);
  const staged = useStagedFiles();

  const groupSize = editing ? Math.max(task.groupTotal || 0, (task.group || []).length) : 0;
  const declined = editing && task.status === "DECLINED";

  // Fill the form each time it opens.
  useEffect(() => {
    if (!open) return;
    setTitle(task?.title || "");
    setDescription(task?.description || "");
    setWhere(task?.outside ? "OUT" : "IN");
    setPeople(task?.assignedToId && !task?.outside ? [String(task.assignedToId)] : []);
    setParty(task?.outsideParty || "");
    setPartyPick(task?.outsidePartyId ? { id: task.outsidePartyId, label: task.outsideParty || "" } : null);
    setPriority(task?.priority || "NORMAL");
    setDueDate(task?.dueDate || "");
    setApplyToGroup(true);
    setError("");
    todoTaskApi.assignableUsers()
      .then((res) => {
        const list = unwrap(res) || [];
        setUsers(list);
        // A new task defaults to yourself; pick colleagues to give it away.
        if (!task) {
          const me = list.find((u) => u.me);
          if (me) setPeople([String(me.id)]);
        }
      })
      .catch(() => setUsers([]));
  }, [open, task]);

  const close = () => {
    if (saving) return;
    staged.discardAll();
    onClose();
  };

  const nameOf = (id) => {
    const u = users.find((x) => String(x.id) === String(id));
    return u ? (u.me ? `${u.name} (me)` : u.name) : `#${id}`;
  };
  const addPerson = (v) => {
    const id = v ? String(v) : "";
    if (id && !people.includes(id)) setPeople([...people, id]);
  };
  const removePerson = (id) => setPeople(people.filter((p) => p !== id));

  const save = async () => {
    if (!title.trim()) { setError("Give the task a title"); return; }
    const outside = where === "OUT";
    // Outside: someone from the list — an older task may keep what was typed.
    const typedOnly = editing && !partyPick && party.trim();
    if (outside && !partyPick && !typedOnly) { setError("Pick who does it outside the factory"); return; }
    if (!outside && people.length === 0) { setError("Choose who the task is for"); return; }
    setSaving(true);
    setError("");
    const common = {
      title: title.trim(),
      description: description.trim() || null,
      priority,
      dueDate: dueDate || null,
    };
    const who = partyPick
      ? { outsidePartyId: partyPick.id, outsideParty: partyPick.label }
      : { outsideParty: party.trim() };
    let body;
    if (editing) {
      body = outside
        ? { ...common, ...who, applyToGroup }
        : { ...common, assignedToId: Number(people[0]), applyToGroup };
    } else {
      body = outside
        ? { ...common, outside: true, ...who, attachmentIds: staged.images.map((i) => i.id) }
        : { ...common, assignedToIds: people.map(Number), attachmentIds: staged.images.map((i) => i.id) };
    }
    try {
      const saved = unwrap(editing ? await todoTaskApi.edit(task.id, body) : await todoTaskApi.create(body));
      staged.clear();
      notifyTodoChanged();
      onSaved?.(saved);
    } catch (e) {
      setError(errMsg(e, "Could not save the task"));
    } finally {
      setSaving(false);
    }
  };

  const options = users
    .filter((u) => !people.includes(String(u.id)))
    .map((u) => ({ value: String(u.id), label: u.me ? `${u.name} (me)` : u.name }));
  const singleOptions = users.map((u) => ({ value: String(u.id), label: u.me ? `${u.name} (me)` : u.name }));

  return (
    <>
      <Modal open={open} onClose={close} width={580}
        title={editing ? `Edit ${task.taskNumber}` : "New task"}
        subtitle={editing
          ? (task.outside ? "Outside the factory — you mark it done or not done"
            : declined ? `${task.assignedToName} declined it${task.declineReason ? `: ${task.declineReason}` : ""} — saving offers it again, to them or the person you choose`
              : "Giving it to someone else starts it afresh for them — they accept it again")
          : "Give work to one or more colleagues, to yourself, or outside the factory"}
        footer={
          <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
            <Btn variant="secondary" onClick={close} disabled={saving}>Cancel</Btn>
            <Btn onClick={save} disabled={saving || staged.uploading > 0}>
              {saving ? "Saving…"
                : declined ? "Offer again"
                : editing ? "Save changes"
                  : where === "OUT" ? "Add to my list"
                    : people.length > 1 ? `Assign to ${people.length} people` : "Assign task"}
            </Btn>
          </div>
        }>
        <Field label="What needs doing">
          <input autoFocus value={title} maxLength={200} onChange={(e) => setTitle(e.target.value)}
            placeholder="e.g. Send 40 trousers to the school" style={inputStyle} />
        </Field>
        <Field label="Details" hint="Optional — anything the person needs to know">
          <textarea value={description} onChange={(e) => setDescription(e.target.value)} rows={3}
            style={{ ...inputStyle, resize: "vertical" }} />
        </Field>

        {/* WHO DOES IT — chosen when the task is made; an edit keeps the kind. */}
        {!editing && (
          <Field label="Who does it">
            <div style={{ display: "flex", gap: 6 }}>
              {[["IN", "In the factory"], ["OUT", "Outside the factory"]].map(([k, label]) => {
                const on = where === k;
                return (
                  <button key={k} type="button" onClick={() => setWhere(k)} style={{
                    flex: 1, padding: "8px 10px", borderRadius: 8, cursor: "pointer", fontSize: 13, fontWeight: 700,
                    fontFamily: "inherit", border: `1px solid ${on ? "var(--primary)" : "var(--border)"}`,
                    background: on ? "var(--primary)" : "var(--bg-input)", color: on ? "#fff" : "var(--text-1)",
                  }}>{label}</button>
                );
              })}
            </div>
          </Field>
        )}

        {where === "OUT" ? (
          <Field label="Outside — who / where"
            hint="They have no login: the task goes on YOUR list and you mark it done or not done yourself.">
            <OutsidePartyPicker value={partyPick ? String(partyPick.id) : ""} disabled={saving}
              onChange={(p) => { setPartyPick(p ? { id: p.id, label: p.label || p.name } : null); if (p) setError(""); }}
              current={editing && task.outsidePartyId ? { id: task.outsidePartyId, label: task.outsideParty || "" } : null}
              legacyText={editing && !task.outsidePartyId ? task.outsideParty : ""} />
          </Field>
        ) : editing ? (
          <Field label="For">
            <Select2 value={people[0] || ""} onChange={(v) => setPeople(v ? [String(v)] : [])}
              options={singleOptions} placeholder="Choose a person…" />
          </Field>
        ) : (
          <Field label="For" hint={people.length > 1
            ? `Each of the ${people.length} gets their own copy — each accepts it with the date they will finish by (or declines), works on it, and you check each one.`
            : "They accept it with the date they will finish by, or decline it with a reason. Add more people to give each of them the same task."}>
            <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: people.length ? 8 : 0 }}>
              {people.map((id) => (
                <span key={id} style={{
                  display: "inline-flex", alignItems: "center", gap: 5, padding: "4px 6px 4px 10px", borderRadius: 16,
                  background: "var(--primary-light)", color: "var(--text-0)", fontSize: 13, fontWeight: 600,
                  border: "1px solid var(--border)",
                }}>
                  {nameOf(id)}
                  <button type="button" title="Remove" onClick={() => removePerson(id)} style={{
                    border: "none", background: "none", cursor: "pointer", color: "var(--text-2)", padding: 0,
                    display: "inline-flex",
                  }}><X size={13} /></button>
                </span>
              ))}
            </div>
            <Select2 value="" onChange={addPerson} options={options}
              placeholder={people.length ? "Add another person…" : "Choose a person…"} />
          </Field>
        )}

        <div style={{ display: "flex", gap: 14, flexWrap: "wrap" }}>
          <div style={{ flex: "1 1 220px" }}>
            <Field label="Priority">
              <div style={{ display: "flex", gap: 6 }}>
                {PRIORITIES.map((p) => {
                  const on = priority === p;
                  return (
                    <button key={p} type="button" onClick={() => setPriority(p)} style={{
                      flex: 1, padding: "7px 8px", borderRadius: 8, cursor: "pointer", fontSize: 13, fontWeight: 600,
                      border: `1px solid ${on ? PRIORITY[p].fg : "var(--border)"}`,
                      background: on ? PRIORITY[p].bg : "var(--bg-input)", color: on ? PRIORITY[p].fg : "var(--text-1)",
                    }}>{PRIORITY[p].label}</button>
                  );
                })}
              </div>
            </Field>
          </div>
          <div style={{ flex: "1 1 160px" }}>
            <Field label="Due date" hint="Optional — overdue tasks go to the top">
              <input type="date" value={dueDate} onChange={(e) => setDueDate(e.target.value)} style={inputStyle} />
            </Field>
          </div>
        </div>
        {editing && groupSize > 1 && (
          <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, color: "var(--text-1)", margin: "2px 0 12px", cursor: "pointer" }}>
            <input type="checkbox" checked={applyToGroup} onChange={(e) => setApplyToGroup(e.target.checked)} />
            Also change the title, details, priority and due date on the other copies ({groupSize} people)
          </label>
        )}
        {!editing && (
          <Field label="Files" last>
            <ImageTray staged={staged} onOpen={setLightbox} disabled={saving} />
          </Field>
        )}
        {error && (
          <div style={{ marginTop: 12, padding: "8px 12px", borderRadius: 8, background: "var(--danger-bg)", color: "var(--danger)", fontSize: 13 }}>
            {error}
          </div>
        )}
      </Modal>
      <Lightbox url={lightbox} onClose={() => setLightbox(null)} />
    </>
  );
}
